import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getServiceUser, getStaff } from "../services/users";
import Spinner from "../components/PageLoader";
import UploadModal from "../components/modals/UploadModal";
import { downloadFileFromSignedUrl } from "../services/util";
import { dateFormat } from "../utils/functions";
import DocxIcon from "../assets/svgs/doc-icon.svg";
import ExcelIcon from "../assets/svgs/excel-icon.svg";
import PdfIcon from "../assets/svgs/pdf-icon.svg";
import JpegIcon from "../assets/svgs/jpeg-icon.svg";
import PngIcon from "../assets/images/png-icon.png";
import { PiFilesFill } from "react-icons/pi";
import { useTheme } from "../context/ThemeContext.js";

export default function Documents({ setPageTitle }) {
  const { theme } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchParams] = useSearchParams();

  const urlParam = useParams();

  const userType = searchParams.get("userType");

  const handleSelect = (row) => {
    setSelectedRow(row);
  };

  const fetchUser = async () => {
    setIsLoading(true);
    try {
      const { data } =
        userType === "staff"
          ? await getStaff(urlParam.id)
          : await getServiceUser(urlParam.id);
      setDocuments(data.documents);
      setPageTitle(data.firstName + " " + data.lastName + " - Documents");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = () => {};

  const onDelete = () => {};

  const onDownload = async () => {
    if (!selectedRow) return;

    setIsLoading(true);
    try {
      const file = await downloadFileFromSignedUrl(selectedRow.fileKey);
      downloadFile(file.data, selectedRow.fileName);
      console.log(file);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const downloadFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob(); // Convert response to blob
    const blobUrl = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename || "download";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    URL.revokeObjectURL(blobUrl); // Clean up memory
    setIsLoading(false);
  };
  const getFileExtension = (fileName) => {
    // Split the file name by '.' and get the last part
    return fileName.split(".").pop();
  };
  const getFileIconClass = (extension) => {
    switch (extension.toLowerCase()) {
      case "pdf":
        return <img src={PdfIcon} width={25} alt="pdf" />;
      case "doc":
      case "docx":
        return <img src={DocxIcon} width={25} alt="docx" />;
      case "xls":
      case "xlsx":
        return <img src={ExcelIcon} width={25} alt="Excel" />;
      case "jpg":
      case "jpeg":
        return <img src={JpegIcon} width={25} alt="jpeg" />;
      case "png":
        return <img src={PngIcon} width={25} alt="png" />;
      default:
        return <PiFilesFill size={25} />;
    }
  };

  return (
    <div className="documents-container" data-theme={theme}>
      <div>
        <div className="user__top">
          <div>
            <button
              className="btn btn__primary"
              onClick={() => setShowUploadModal(true)}
            >
              + Add document
            </button>
          </div>
        </div>
        <div className="d-flex justify-between">
          <div className="documents__actions">
            <button
              onClick={onEdit}
              className="btn btn__link"
              disabled={!selectedRow}
            >
              Edit
            </button>
            <button
              onClick={onDelete}
              className="btn btn__link"
              disabled={!selectedRow}
            >
              Delete
            </button>
            <button
              onClick={onDownload}
              className="btn btn__link"
              disabled={!selectedRow}
            >
              Download
            </button>
          </div>
        </div>

        <div className="documents__list">
          <table className="documents">
            <thead>
              <tr>
                <th>Select</th>
                <th>Name</th>
                <th>Date uploaded</th>
                <th>Expiry date</th>
                <th>Group name</th>
              </tr>
            </thead>

            <tbody>
              {documents.map((row) => (
                <tr
                  key={row.fileName}
                  className={
                    selectedRow?.fileName === row.fileName ? "selected-row" : ""
                  }
                >
                  <td>
                    <input
                      type="radio"
                      name="selectRow"
                      className="documents__radio"
                      checked={selectedRow?.fileName === row.fileName}
                      onChange={() => handleSelect(row)}
                    />
                  </td>
                  <td>
                    <div className="documents__name">
                      {row.fileName}{" "}
                      {getFileIconClass(getFileExtension(row.fileName))}
                    </div>
                  </td>
                  <td>{dateFormat(row?.createdAt, "do MMM, yyyy" || "--")}</td>
                  <td>
                    {dateFormat(row?.expirationDate, "do MMM, yyyy" || "--")}
                  </td>
                  <td>{row.groupName}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isLoading && (
            <div className="documents__loader">
              <Spinner />
            </div>
          )}
        </div>

        {documents.length === 0 && !isLoading && (
          <div className="documents__empty">
            <p>No documents found</p>
          </div>
        )}
      </div>

      {showUploadModal && (
        <UploadModal
          isModalOpen={showUploadModal}
          setIsModalOpen={setShowUploadModal}
          userType={userType}
          userId={urlParam?.id}
          refreshData={fetchUser}
        />
      )}
    </div>
  );
}
